import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms'
import { Store } from '@ngrx/store'
import {
  PayoutFrequency,
  Basis,
  PayoutAgreement,
  CTCData
} from '../../../store/payout-agreement/payout-agreement.state'
import { CommonModule, NgIf } from '@angular/common'
import { IndianNumberInputFormatDirective } from '../../../directives/indian-number-input-format.directive'
import { selectAllCountries } from '../../../store/country/country.selectors'
import { loadCountries } from '../../../store/country/country.actions'
import { payoutAgreementActions } from '../../../store/payout-agreement/payout-agreement.actions'
import { splitUnderscoreAndCapitalize } from '../../../utils/payout-agreement.utils'
import { filter, Subscription, take, tap } from 'rxjs'
import { selectSelectedUser } from '../../../store/user/user.reducer'
import { User } from '../../../store/auth/auth.state'
import dayjs from 'dayjs'
import { selectPayout } from '../../../store/payout-agreement/payout-agreement.reducers'

@Component({
  selector: 'app-payout-agreement',
  imports: [NgIf, ReactiveFormsModule, CommonModule, IndianNumberInputFormatDirective],
  templateUrl: './payout-agreement.component.html',
  styleUrl: './payout-agreement.component.scss'
})
export class PayoutAgreementComponent implements OnInit, OnDestroy {
  store = inject(Store)
  form!: FormGroup
  fb = inject(FormBuilder)
  @Output() formValuesChanged = new EventEmitter<CTCData>()
  private subscription!: Subscription
  private joiningDate!: string | Date
  payoutFrequency = PayoutFrequency
  payoutFrequencyValues = Object.values(PayoutFrequency)
  basis = Basis
  basisValues = Object.values(Basis)
  countries$ = this.store.select(selectAllCountries)
  formatEnumValues = splitUnderscoreAndCapitalize

  async ngOnInit() {
    this.store.dispatch(loadCountries())
    this.initializeForm()

    // Get the joining date
    // Latest Selected user was already dispatched in the wrapper component
    this.store
      .select(selectSelectedUser)
      .pipe(
        filter((user): user is User => !!user),
        take(1),
        tap(user => {
          if (user.joiningDate) {
            this.joiningDate = dayjs(user.joiningDate).format('YYYY-MM-DD')
          } else {
            this.joiningDate = dayjs().format('YYYY-MM-DD')
          }
        })
      )
      .subscribe()

    // Patch form with store data
    this.store
      .select(selectPayout)
      .pipe(take(1))
      .subscribe(payout => {
        const patch = {
          payoutFrequency: payout?.payoutFrequency ?? this.payoutFrequency.BI_WEEKLY,
          currency: payout?.currency ?? 'INR',
          ctc: payout?.ctc ?? 0,
          basis: payout?.basis ?? this.basis.HOURLY,
          effectiveFrom: payout?.effectiveFrom ?? '',
          payoutFrom: payout?.payoutFrom ?? ''
        }
        this.form.patchValue(patch)
      })

    // Emit live ctc value for Parent Component
    this.subscription =
      this.form.valueChanges.subscribe(value => {
        this.formValuesChanged.emit(value)
      }) ?? new Subscription()
  }

  ngOnDestroy(): void {
    this.form.patchValue({
      effectiveFrom: this.joiningDate
    })
    this.form.patchValue({
      payoutFrom: dayjs(this.joiningDate).format('YYYY-MM')
    })
    this.store.dispatch(payoutAgreementActions.addCTCData(this.form.value))
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  initializeForm() {
    this.form = this.fb.group({
      payoutFrequency: [this.payoutFrequency.BI_WEEKLY, Validators.required],
      currency: ['INR', Validators.required],
      ctc: [0, Validators.required],
      basis: [this.basis.HOURLY, Validators.required],
      effectiveFrom: ['', Validators.required],
      payoutFrom: ['', Validators.required]
    })
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }
}
